import { z } from 'zod';
import { textSchema } from '../task/task';

/**
 * One document's section in the catalogue (QZ-D104). `jobs` and `approved` count that section's
 * jobs and approved tasks only, not the document's as a whole.
 */
export const catalogueSectionSchema = z
  .object({ name: textSchema, jobs: z.number().int().min(0), approved: z.number().int().min(0) })
  .strip();
export type CatalogueSection = z.infer<typeof catalogueSectionSchema>;

/**
 * One document in the catalogue (QZ-D104). `jobs` and `approved` cover all of the document's
 * jobs and approved tasks, sectioned or not; a section's own counts are in `sections`.
 */
export const catalogueDocumentSchema = z
  .object({
    document: textSchema,
    jobs: z.number().int().min(0),
    approved: z.number().int().min(0),
    sections: z.array(catalogueSectionSchema),
  })
  .strip();
export type CatalogueDocument = z.infer<typeof catalogueDocumentSchema>;

/**
 * The order every list of sections sorts in, API and web alike (QZ-D101): digit runs compare as
 * numbers, so "Kapitel 2" sorts before "Kapitel 10"; letters compare case-insensitively. Roman
 * numerals do not sort numerically — "Kapitel IX" sorts before "Kapitel X" only alphabetically.
 */
export const compareSectionNames = (a: string, b: string): number =>
  a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });

/**
 * A new array of `sections`, sorted by name in `compareSectionNames` order; the argument is left
 * unchanged.
 */
export const sortSections = (sections: readonly CatalogueSection[]): CatalogueSection[] =>
  [...sections].sort((a, b) => compareSectionNames(a.name, b.name));
